import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'App/hooks';
import { useAppDispatch } from 'App/store';
import { useBoolean, useToastStatus } from 'commons/hooks';
import { useGetUserInfo } from 'domains/Auth/hooks';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';
import {
  cleanForwardConfirmationEmail,
  forwardConfirmationEmailAction,
  getforwardConfirmationEmailStatus,
} from 'domains/Dashboard/slices/usersSlice';
import { GenericTaskItem } from '../GenericTaskList';

export interface UseEmailVerificationPromoteResult {
  item: GenericTaskItem | null;
  handleAction: () => void;
  handleSkip: () => void;
}

function useEmailVerificationPromote(): UseEmailVerificationPromoteResult {
  const dispatch = useAppDispatch();
  const verifyUserConfirmation = useQuery().get('userConfirmed');
  const { userConfirmed, email } = useGetUserInfo();
  const [showAlert, openAlert, closeAlert] = useBoolean();
  const emailStatus = useSelector(getforwardConfirmationEmailStatus);
  const tEmail = useTranslationDashboard('email-not-confirmed');
  const t = useTranslationDashboard();

  const forwardConfirmationEmail = useCallback(async () => {
    await dispatch(forwardConfirmationEmailAction());
    dispatch(cleanForwardConfirmationEmail());
  }, [dispatch]);

  useToastStatus({
    status: emailStatus,
    success: tEmail('success'),
    error: tEmail('error'),
  });

  useEffect(() => {
    if (!userConfirmed && !verifyUserConfirmation) openAlert();
  }, [openAlert, userConfirmed, verifyUserConfirmation]);

  const handleAction = useCallback(() => {
    forwardConfirmationEmail();
  }, [forwardConfirmationEmail]);

  const handleSkip = useCallback(() => {
    closeAlert();
  }, [closeAlert]);

  const showEmailNotConfirmedAlert =
    userConfirmed !== undefined && !userConfirmed && showAlert;
  const shouldShowVerifyEmail =
    !verifyUserConfirmation && showEmailNotConfirmedAlert;

  const item: GenericTaskItem | null = shouldShowVerifyEmail
    ? {
        id: 'verify-email',
        title: t('promote.emailNotConfirmed.title'),
        description: t('promote.emailNotConfirmed.description', { email }),
        iconType: 'info',
        contentType: 'recommendation',
        actionType: 'alert',
        ctaText: t('promote.emailNotConfirmed.cta'),
        ctaLink: '/promote/campaign',
      }
    : null;

  return {
    item,
    handleAction,
    handleSkip,
  };
}

export default useEmailVerificationPromote;
